import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

/**
 * What a partner facility sees about itself.
 *
 * Public by design — the token in the URL is the credential, because facilities
 * have no accounts. Everything rendered here is aggregate and was already
 * suppressed when it was written, so there is nothing on this page that could
 * identify a person even if the link is forwarded.
 *
 * Written as a page someone could put in front of a practice manager: the
 * privacy promise is stated plainly rather than buried, because it is the first
 * objection any clinic raises and the honest answer is a good one.
 */
const API = process.env.REACT_APP_API_URL || '/api';

const COHORTS = [
  { key: 'patient', label: 'Patients', blurb: 'People who scanned your card' },
  { key: 'staff', label: 'Staff', blurb: 'Your team, on seats you provide' },
];

export default function FacilityReportPage() {
  const { token } = useParams();
  const [state, setState] = useState({ status: 'loading' });

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const res = await fetch(`${API}/org-report/${encodeURIComponent(token)}`);
        const body = await res.json().catch(() => ({}));
        if (!live) return;
        setState(res.ok
          ? { status: 'ok', data: body }
          : { status: 'denied', message: body.error || 'This report link is not valid.' });
      } catch {
        if (live) setState({ status: 'denied', message: 'We could not load this report. Check your connection and try again.' });
      }
    })();
    return () => { live = false; };
  }, [token]);

  if (state.status === 'loading') {
    return <Shell><p className="text-slate-500">Loading…</p></Shell>;
  }

  if (state.status === 'denied') {
    return (
      <Shell>
        <h1 className="text-xl font-bold text-slate-800">This link isn’t valid</h1>
        <p className="text-slate-600 mt-2 max-w-prose">{state.message}</p>
        <p className="text-slate-500 text-sm mt-4">
          Report links expire, and can be turned off. Ask your HealthClear AI contact for a new one.
        </p>
      </Shell>
    );
  }

  const { organization, totals, series, expiresAt, notice } = state.data;
  const hasData = Array.isArray(series) && series.length > 0;

  return (
    <Shell>
      <header className="border-b border-slate-200 pb-5">
        <p className="text-xs font-semibold uppercase tracking-widest text-cyan-700">HealthClear AI</p>
        <h1 className="text-2xl font-bold text-slate-900 mt-1">{organization}</h1>
        <p className="text-slate-500 text-sm mt-1">
          Utilisation to date{expiresAt ? ` · link valid until ${new Date(expiresAt).toLocaleDateString()}` : ''}
        </p>
      </header>

      {!hasData ? (
        <p className="text-slate-600 mt-6">
          Nothing to show yet. Figures appear the day after your first code is used.
        </p>
      ) : (
        <div className="grid gap-5 sm:grid-cols-2 mt-6">
          {COHORTS.map(({ key, label, blurb }) => {
            const t = totals?.[key];
            if (!t) return null;
            const stats = [
              ['Codes redeemed', t.redemptions],
              ['Results explained', t.partial ? `${t.reportsAnalyzed}+` : t.reportsAnalyzed],
              ['Busiest single day', t.peakDailyActiveUsers],
              ['Understanding score', t.avgComprehension === null ? '—' : `${t.avgComprehension}%`],
            ];
            return (
              <section key={key} className="rounded-2xl border border-slate-200 p-5 bg-white">
                <h2 className="font-semibold text-slate-800">{label}</h2>
                <p className="text-xs text-slate-500 mb-4">{blurb}</p>
                <dl className="grid grid-cols-2 gap-4">
                  {stats.map(([l, v]) => (
                    <div key={l}>
                      <dt className="text-xs text-slate-500">{l}</dt>
                      <dd className="text-2xl font-bold text-slate-900 tabular-nums">{v}</dd>
                    </div>
                  ))}
                </dl>
                {t.partial && (
                  <p className="text-xs text-slate-500 mt-4 leading-relaxed">
                    {t.suppressedDays} day{t.suppressedDays === 1 ? '' : 's'} withheld — too few people
                    to report without identifying them. These totals are a floor, not a ceiling.
                  </p>
                )}
              </section>
            );
          })}
        </div>
      )}

      <footer className="mt-8 pt-5 border-t border-slate-200">
        <p className="text-sm text-slate-600 max-w-prose leading-relaxed">{notice}</p>
        <p className="text-xs text-slate-400 mt-3">
          Patients upload their own results to HealthClear AI. Your practice discloses nothing to us,
          and we never share what any individual’s results say — with you or anyone else.
        </p>
      </footer>
    </Shell>
  );
}

function Shell({ children }) {
  return (
    <div className="min-h-screen bg-slate-50 py-10 px-5">
      <div className="max-w-3xl mx-auto">{children}</div>
    </div>
  );
}
